import { useMemo } from 'react';

import { ConsumerBackerIntent, useShowConsumerBackerIntent } from '@givebacks/api/backer';
import { IListUserCauseSelectionsResponse, useListUserCauseSelections } from '@givebacks/api/core';

interface UseUserIntentFetchReturn {
  fetchedIntents: ConsumerBackerIntent;
  hasMoreThanOneCause: boolean;
  userCauseSelections: IListUserCauseSelectionsResponse['cause_selections'] | undefined;
  isLoading: boolean;
}

const EMPTY_FETCHED_INTENTS = {
  buy_a_membership: false,
  support_a_fundraiser: false,
  buy_a_store_item: false,
  check_out_shop_to_give: false,
  pay_a_student_fee: false,
  buy_a_ticket: false,
  make_a_donation: false,
  view_membership_card: false,
  view_event_tickets: false,
  view_purchase_history: false,
  view_cash_back_earned: false,
};

/**
 * Handles data fetching and provides fetched user intents
 */
export const useUserIntentFetch = (): UseUserIntentFetchReturn => {
  const { data: userCauseSelections, isFetching: isFetchingCauses } = useListUserCauseSelections();
  const { data: userIntentData, isFetching: isFetchingUserIntent } = useShowConsumerBackerIntent();

  const isLoading = isFetchingCauses || isFetchingUserIntent;

  const hasMoreThanOneCause = useMemo(() => {
    return Boolean(userCauseSelections?.length && userCauseSelections.length > 1);
  }, [userCauseSelections]);

  //Return empty intents while we wait for the user intent data to be fetched
  const fetchedIntents = useMemo(() => {
    return userIntentData && userIntentData?.intent
      ? userIntentData?.intent
      : EMPTY_FETCHED_INTENTS;
  }, [userIntentData]);

  return {
    fetchedIntents,
    hasMoreThanOneCause,
    userCauseSelections,
    isLoading,
  };
};
